import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@jobenvoy/ui";
import { describeError } from "@/lib/api/client";
import {
    getEnvoyArea,
    placeEnvoyAreaHere,
    saveEnvoyArea,
    type EnvoyAreaRequest,
} from "@/lib/api/envoy";
import { track } from "@/lib/analytics";
import { claimAreaHere, releaseAreaHere } from "@/lib/areaHere";
import { pickableCities, readArea, type KnownArea } from "@/lib/envoyArea";
import { usePlaces } from "./catalogue";
import { useDebounced } from "./debounce";
import { keys } from "./keys";
import { ONE_AT_A_TIME, useSingleFlightMutation } from "./singleFlight";

/** Where the person's envoy looks, or null until the edge or the person says a city. */
export function useEnvoyArea(enabled = true) {
    return useQuery({
        queryKey: keys.envoy.area,
        queryFn: async (): Promise<KnownArea | null> => readArea((await getEnvoyArea())?.area),
        enabled,
    });
}

/** Saves the area the person says. A failure is said in a toast; the editor stays open. */
export function useSaveEnvoyArea() {
    const client = useQueryClient();
    const toast = useToast();
    return useSingleFlightMutation(
        {
            mutationFn: (area: EnvoyAreaRequest) => saveEnvoyArea(area),
            onSuccess: async (answer) => {
                track({ name: "envoy_area_saved" });
                // A read still in flight would put back what was there before.
                await client.cancelQueries({ queryKey: keys.envoy.area });
                client.setQueryData(keys.envoy.area, readArea(answer?.area));
                toast.success("Saved", "Your envoy looks around this city from now on.");
            },
            onError: (error) => toast.error("Not saved", describeError(error)),
        },
        ONE_AT_A_TIME,
    );
}

/**
 * Tells envoy where the browser is, once a browser session for the person
 * signed in. Quiet: a failure says nothing, and the next time the
 * dashboard opens it tries again. An answer from the edge never covers a
 * city the person set while it was on its way.
 */
export function useAreaHereOnce(userId: string) {
    const client = useQueryClient();
    useEffect(() => {
        if (!claimAreaHere(userId)) return;
        placeEnvoyAreaHere()
            .then((answer) => {
                const said = readArea(answer?.area);
                client.setQueryData<KnownArea | null>(keys.envoy.area, (current) =>
                    current?.by === "PERSON" && said?.by !== "PERSON" ? current : said,
                );
            })
            .catch(() => releaseAreaHere(userId));
    }, [userId, client]);
}

/**
 * The cities the editor offers for what is typed: the catalogue's, and
 * only those it can place on the map. Nothing is asked under two letters.
 */
export function useAreaCities(typed: string) {
    const settled = useDebounced(typed.trim());
    const q = settled.split(",")[0]?.trim() ?? "";
    const enabled = q.length >= 2;
    const cities = usePlaces({ level: "CITY", q, limit: 8 }, enabled);
    return {
        cities: enabled && !cities.isPlaceholderData ? pickableCities(cities.data?.data ?? []) : [],
        loading: typed.trim() !== settled || (enabled && cities.isFetching),
        failed: enabled && cities.isError,
        short: typed.trim().length < 2,
    };
}
